
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useState, type ReactNode } from "react";
import type { PageLocale } from "@/utils/pageLocale";
import "./Navigation.css";
import { trackClick } from "@/utils/posthog";
import { useLocation } from "react-router-dom";
import { useHydratedSearch } from "@/hooks/useHydratedSearch";
import { buildGrowthJourneyUrl } from "@/utils/growthJourney";

interface NavigationProps {
  hideCTA?: boolean;
  variant?: "dark" | "light";
  logoSrc?: string;
  logoAlt?: string;
  logoClassName?: string;
  locale?: PageLocale;
  children?: ReactNode;
}

const variantClasses = {
  dark: {
    nav: "bg-background/95 border-border",
    menuButton: "text-foreground hover:text-primary",
    mobileMenu: "bg-background border-border",
  },
  light: {
    nav: "bg-longhand-paper/95 border-longhand-border",
    menuButton: "text-longhand-ink hover:text-longhand-accent",
    mobileMenu: "bg-longhand-paper border-longhand-border",
  },
} as const;

const Navigation = ({
  hideCTA = false,
  variant = "dark",
  logoSrc = "/orange-logo.png",
  logoAlt = "Tech Leaders",
  logoClassName,
  locale = "en",
  children,
}: NavigationProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const styles = variantClasses[variant];
  const search = useHydratedSearch();
  const { pathname } = useLocation();
  const source = pathname === "/" ? "home" : pathname.slice(1).replaceAll("/", "_");
  const librarySearch = new URLSearchParams(search);
  const isTeamRoute = ["/ai-30", "/ai-30/summary", "/ai-for-leaders", "/ai-for-leaders/summary"].includes(pathname.replace(/\/$/, ""));
  if (isTeamRoute) librarySearch.set("audience", "organization");
  const libraryHref = (location: string) => buildGrowthJourneyUrl("/library", librarySearch.toString(), { source, location });

  return (
    <nav className={`fixed top-0 w-full backdrop-blur-md z-50 border-b ${styles.nav}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex min-h-16 flex-wrap justify-between items-center gap-x-3 py-2">
          <div className="flex items-center">
            <a href={buildGrowthJourneyUrl("/", search)} aria-label={locale === "pt-BR" ? "Tech Leaders: início (em inglês)" : "Tech Leaders home"}><img src={logoSrc} alt={logoAlt} className={logoClassName ?? (children ? "h-7 w-auto sm:h-10" : "h-10 w-auto")} /></a>
          </div>

          {!hideCTA && (
            <div className={children ? "hidden xl:flex items-center space-x-4" : "hidden md:flex items-center space-x-4"}>
              <Button
                asChild
                className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold"
              >
                <a href={libraryHref("navigation")} onClick={() => trackClick("Fortune 100 AI Prompt Library", { location: "navigation" })}>
                  {locale === "pt-BR" ? "Acessar a biblioteca de prompts Fortune 100 (em inglês)" : "Get the Fortune 100 AI Prompt Library"}
                </a>
              </Button>
            </div>
          )}

          <div className={children ? "flex items-center gap-2" : hideCTA ? "hidden" : "flex items-center gap-2 md:hidden"}>
            {children}
            {!hideCTA && (
            <div className={children ? "xl:hidden" : "md:hidden"}>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`${styles.menuButton} inline-flex min-h-11 min-w-11 items-center justify-center`}
                aria-label={locale === "pt-BR" ? "Menu de navegação" : "Navigation menu"}
                aria-expanded={isMenuOpen}
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
            )}
          </div>
        </div>
      </div>

      {!hideCTA && isMenuOpen && (
        <div className={`${children ? "xl:hidden" : "md:hidden"} border-b ${styles.mobileMenu}`}>
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <div className="px-3 py-2">
              <Button
                asChild
                className="h-auto min-h-10 w-full whitespace-normal bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold"
              >
                <a href={libraryHref("mobile_navigation")} onClick={() => trackClick("Fortune 100 AI Prompt Library", { location: "mobile_navigation" })}>
                  {locale === "pt-BR" ? "Biblioteca de prompts Fortune 100 (em inglês)" : "Fortune 100 AI Prompt Library"}
                </a>
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
